import { useCallback, useEffect, useState } from "react";
import {
	Modal,
	Button,
	Dropdown,
	DropdownMenuButton,
	Icon,
	Loading,
} from "@alm/flow-ds/components";
import { useTranslation } from "react-i18next";
import FormID from "components/FormDesigner/FormID";
import api from "lib/api";

type Props = {
	saveForm: (saveAs: string | undefined) => Promise<void>;
	elementsLength: number;
	currentFormId?: string;
};

export default function SaveDialog({
	saveForm,
	elementsLength,
	currentFormId,
}: Props) {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState(false);

	const [saveLoading, setSaveLoading] = useState(false);
	const [saveAsLoading, setSaveAsLoading] = useState(false);
	const [saveAsReady, setSaveAsReady] = useState<boolean>(false);

	// Is the modal doing a 'save as'
	const [isSaveAs, setIsSaveAs] = useState<boolean>(false);

	// Form id that the user types into the textinput
	const [newFid, setNewFid] = useState<string | undefined>(undefined);

	// Does the form id typed in the 'save as' modal exist
	const [newFidExists, setNewFidExists] = useState<boolean>(false);

	// Does the current form id given to the form exist
	const [currentFidExists, setCurrentFidExists] = useState<boolean>(false);

	// Is the form id valid
	const [isFidValid, setIsFidValid] = useState<boolean>(false);

	/**
	 * Check to see whether newFid exists to determine whether
	 * to show an overwrite modal or a save as modal
	 */
	const checkNewFormId = useCallback(async () => {
		if (newFid && isFidValid) {
			setNewFidExists(
				await api.definitions.getDefinition(newFid, {
					handleErrors: true,
				})
			);
		}
		setSaveAsReady(true);
	}, [newFid, isFidValid]);

	/**
	 * Check to see whether currentFormId exists to determine whether
	 * to save straight away or to show overwrite modal
	 */
	const checkCurrentFormId = useCallback(async () => {
		if (!currentFormId) {
			setCurrentFidExists(true);
		} else {
			await api.definitions.getDefinition(currentFormId, {
				handleErrors: true,
			});
		}
	}, [currentFormId]);

	/**
	 * Save form action
	 */
	const save = useCallback(async () => {
		await saveForm(
			newFid ? newFid.trim() : currentFormId ? currentFormId : undefined
		);
		setIsOpen(false);
	}, [currentFormId, newFid, saveForm]);

	/**
	 * Handler to see whether the form can be saved straight away or not
	 */
	const handleSave = useCallback(async () => {
		setSaveLoading(true);
		if (currentFidExists && currentFormId) {
			setIsSaveAs(false);
			setIsOpen(true);
		} else {
			await save();
		}
		setSaveLoading(false);
	}, [save, currentFidExists, currentFormId]);

	/**
	 * Handler to save as a form
	 */
	const handleSaveAs = useCallback(async () => {
		setSaveAsLoading(true);
		await save();
		setSaveAsLoading(false);
	}, [save]);

	/**
	 * Everytime newFid is changed we need to disable the save button
	 * which will prevent the user from saving without the form id being
	 * checked first
	 */
	useEffect(() => {
		setSaveAsReady(false);
	}, [newFid]);

	/**
	 * Reset newFid and newFidExists when modal is closed
	 */
	useEffect(() => {
		if (!isOpen) {
			setNewFid(undefined);
			setNewFidExists(false);
		}
	}, [isOpen]);

	useEffect(() => {
		checkCurrentFormId();
	}, [checkCurrentFormId]);

	return (
		<>
			<Dropdown
				id="save_dropdown"
				menuBtn={
					<Button
						className="btn-primary"
						label={
							saveLoading
								? t("formDesigner.labels.saving")
								: t("common.labels.save")
						}
						disabled={elementsLength < 1}>
						<Icon
							className="save-arrow"
							type="caret-down"
							weight="s"
						/>
					</Button>
				}
				disabled={elementsLength < 1 || saveLoading}
				align="right">
				<DropdownMenuButton
					onClick={handleSave}
					label={t("common.labels.save")}
				/>
				<DropdownMenuButton
					onClick={() => {
						setIsSaveAs(true);
						setIsOpen(true);
					}}
					label={t("formDesigner.labels.saveAs")}
				/>
			</Dropdown>
			<Modal
				className="save-dialog"
				close={() => setIsOpen(false)}
				isOpen={isOpen}
				title={
					!isSaveAs || newFidExists
						? t("formDesigner.dialogs.overwriteTitle")
						: t(`formDesigner.dialogs.saveTitle`)
				}
				modalSize={isSaveAs ? "sm" : "md"}
				showHeaderClose={false}
				footer={
					isSaveAs && (
						<>
							<Button
								label={t("common.labels.cancel")}
								className="btn-default"
								onClick={() => setIsOpen(false)}
							/>
							<Button
								label={
									!saveAsLoading &&
									(newFidExists
										? t("formDesigner.labels.overwrite")
										: t("common.labels.save"))
								}
								className={`btn-primary save-form-btn ${
									newFidExists
										? "save-form-overwrite-btn"
										: "save-form-btn"
								}`}
								onClick={handleSaveAs}
								disabled={
									!newFid || !isFidValid || !saveAsReady
								}>
								{saveAsLoading && <Loading />}
							</Button>
						</>
					)
				}>
				{isSaveAs ? (
					<FormID
						formId={newFid}
						setFormId={setNewFid}
						isValid={isFidValid}
						setIsValid={setIsFidValid}
						onBlur={checkNewFormId}
					/>
				) : (
					<div className="save-buttons">
						<Button
							label={t("common.labels.cancel")}
							className="btn-default"
							onClick={() => setIsOpen(false)}
						/>
						<Button
							label={
								!saveAsLoading &&
								t("formDesigner.labels.overwrite")
							}
							className="btn-primary save-form-overwrite-btn"
							onClick={handleSaveAs}>
							{saveAsLoading && <Loading />}
						</Button>
						<Button
							label={t("formDesigner.labels.saveAs")}
							className="btn-primary"
							onClick={() => {
								setIsSaveAs(true);
								setIsOpen(true);
							}}
						/>
					</div>
				)}
			</Modal>
		</>
	);
}
